import React, { useEffect, useRef, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { createPortal } from 'react-dom';
import { usePageRenderContext } from '../browser/pagerender/pagerendercontext';
import { useZIndex } from '../browser/zindexcontext';

// ─── Tamanhos ─────────────────────────────────────────────────────────────────

const modalSizes = {
  xs:     { w: 400,    h: 280    },
  sm:     { w: 300,    h: 400    },
  md:     { w: 600,    h: 500    },
  lg:     { w: 900,    h: 600    },
  xl:     { w: 1140,   h: 700    },
  dialog: { w: 400,    h: 200    },
  r:      { w: '100%', h: '100%' },
  full:   { w: '100%', h: '100%' },
} as const;

export type ModalSize = keyof typeof modalSizes;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface EasyModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: ModalSize;
  paginaIDEx?: string;
  zIndexManual?: number;
  disableBackdropClose?: boolean;
}

// ─── Componente ───────────────────────────────────────────────────────────────

const EasyModal: React.FC<EasyModalProps> = ({
  open,
  onClose,
  children,
  size = 'lg',
  paginaIDEx,
  disableBackdropClose = false,
  zIndexManual
}) => {
  const theme    = useTheme();
  const zCtx     = useZIndex();
  const { paginaID } = (usePageRenderContext() as { paginaID?: string }) ?? {};

  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [mounted, setMounted]     = useState(false);
  const [zIndex, setZIndex]       = useState<number>(0);
  const modalRef = useRef<HTMLDivElement>(null);

  // ── Resolve container da página ───────────────────────────────────────────

  useEffect(() => {
    const id = paginaID ?? paginaIDEx ?? '';
    setContainer(id ? (document.getElementById(id) ?? null) : null);
  }, [paginaID, paginaIDEx]);

  // ── Gerencia z-index ao abrir/fechar ─────────────────────────────────────
  //
  // Cada EasyModal que abre consome um nível do contador local da página.
  // Ao fechar, libera o nível — popup dentro de popup funciona corretamente:
  //
  //   Grade header  → z natural do layout (sem modal)
  //   1º EasyModal  → z = 20
  //   2º EasyModal  → z = 30   (popup dentro do 1º)
  //   2º fecha      → release → contador volta a 1
  //   1º fecha      → release → contador volta a 0

  useEffect(() => {
    if (open) {
      const z = zCtx.next();
      if(z > (zIndexManual ?? 0)) {setZIndex(z)}
        else {
          setZIndex(zIndexManual ?? 0);}
    } else {
      if (zIndex > 0) zCtx.release();
      setZIndex(0);
    }
    // zCtx é estável (useCallback), zIndex causaria loop — intencionalmente omitido
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Animação de entrada ───────────────────────────────────────────────────

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => setMounted(true), 10);
      return () => clearTimeout(t);
    }
    setMounted(false);
  }, [open]);

  // ── Focus ao abrir ────────────────────────────────────────────────────────

  useEffect(() => {
    if (open && mounted) modalRef.current?.focus();
  }, [open, mounted]);

  if (!open || !container) return null;

  const baseSize = modalSizes[size];

  return createPortal(
    <Box
      role="presentation"
      sx={{
        position: 'absolute',
        inset: 0,
        bgcolor: mounted ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0)',
        transition: 'background-color 180ms ease',
        zIndex,                 // ← local da página, gerenciado pelo ZIndexContext
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 1, sm: 2 },
        overflow: 'hidden',
      }}
      onClick={disableBackdropClose ? undefined : onClose}
      onKeyDown={(ev) => {
        if (ev.key === 'Escape') { ev.stopPropagation(); onClose(); }
      }}
    >
      <Box
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderRadius: 3,
          boxShadow: theme.shadows[24],
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth:  typeof baseSize.w === 'number' ? `${baseSize.w}px` : baseSize.w,
          height: '100%',
          maxHeight: typeof baseSize.h === 'number' ? `${baseSize.h}px` : baseSize.h,
          minWidth: 0,
          minHeight: 0,
          overflow: 'hidden',
          opacity:   mounted ? 1 : 0,
          transform: mounted ? 'scale(1)' : 'scale(0.96)',
          transition: 'opacity 180ms ease, transform 180ms ease',
          '& > *': { flex: 1, minHeight: 0, overflow: 'auto' },
          '&:focus': { outline: 'none' },
        }}
        data-testid="easy-modal-root"
      >
        {children}
      </Box>
    </Box>,
    container
  );
};

export default EasyModal;